import { createDurableLiveUpdatesController } from "@/lib/data/live-updates/controller";
import {
  notificationInboxStreamFrameSchema,
  type NotificationInboxChangeFrame,
} from "@/lib/data/live-updates/notification-inbox/contract";
import {
  deleteNotificationInboxItem,
  updateNotificationInboxItemsReadState,
  type NotificationInboxFilters,
  type NotificationInboxPage,
  type NotificationInboxItem,
  updateNotificationInboxReadState,
} from "@/lib/data/requests/matters/matter-notification";
import { queryClient as defaultQueryClient } from "@/lib/data/react-query/client";
import {
  fetchNotificationInboxPage,
  getNotificationInboxQueryKey,
  getNotificationInboxInfiniteQueryKey,
  refreshNotificationInbox,
} from "./notification-inbox-refresh";
import {
  VITE_FAST_API_URL,
  VITE_NOTIFICATION_STREAM_URL,
} from "@/lib/data/static/importEnv";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { fetchAuthSession } from "@aws-amplify/auth";
import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

const notificationStreamSubprotocol = "lawworks-api.notification-inbox.v2";

export const getNotificationInboxItemsOptions = ({
  enabled = true,
  filters = {},
  queryClient = defaultQueryClient,
}: {
  enabled?: boolean;
  filters?: NotificationInboxFilters;
  queryClient?: QueryClient;
} = {}) => ({
  queryKey: getNotificationInboxQueryKey(filters),
  queryFn: () => fetchNotificationInboxPage(queryClient, filters),
  enabled,
  placeholderData: (
    previousData: APIResponse<NotificationInboxPage> | undefined,
  ) => previousData,
});

export const getInfiniteNotificationInboxItemsOptions = (
  filters: NotificationInboxFilters = {},
  queryClient: QueryClient = defaultQueryClient,
) => ({
  queryKey: getNotificationInboxInfiniteQueryKey(filters),
  queryFn: ({ pageParam }: { pageParam: string | null }) =>
    fetchNotificationInboxPage(queryClient, {
      ...filters,
      cursor: pageParam ?? undefined,
    }),
  initialPageParam: null as string | null,
  getNextPageParam: (lastPage: APIResponse<NotificationInboxPage>) =>
    lastPage.pagination?.nextCursor ?? undefined,
});

const getAccessToken = async (forceRefresh = false) => {
  const session = await fetchAuthSession(
    forceRefresh ? { forceRefresh: true } : undefined,
  );
  const token = session.tokens?.accessToken.toString();
  if (!token) throw new Error("Missing Cognito access token");
  return token;
};

const buildNotificationInboxStreamUrl = () => {
  const baseUrl = VITE_NOTIFICATION_STREAM_URL || VITE_FAST_API_URL;
  if (typeof baseUrl !== "string" || !baseUrl) {
    throw new Error("Missing FastAPI URL");
  }
  const url = new URL("/matters/v3/notifications/stream", baseUrl);
  url.protocol = ["https:", "wss:"].includes(url.protocol) ? "wss:" : "ws:";
  // Omitting `after` subscribes at the current server head without replay.
  // Inbox snapshots catch up silently; only this live connection drives toasts.
  return url.toString();
};

type ReceivedNotificationChange = NotificationInboxChangeFrame & {
  connectionGeneration: number;
};

/** Refresh historical inbox state silently and present only live creations. */
export const useNotificationInboxStream = (
  onLiveNotifications?: (notifications: NotificationInboxItem[]) => void,
) => {
  const queryClient = useQueryClient();
  const [hasSnapshot, setHasSnapshot] = useState(false);
  const onLiveNotificationsRef = useRef(onLiveNotifications);
  useEffect(() => {
    onLiveNotificationsRef.current = onLiveNotifications;
  }, [onLiveNotifications]);

  useEffect(() => {
    let disposed = false;
    let bootstrapAttempt = 0;
    let bootstrapTimer: number | undefined;
    let connectionGeneration = 0;
    const presentedIds = new Set<string>();
    let controller:
      | ReturnType<
          typeof createDurableLiveUpdatesController<ReceivedNotificationChange>
        >
      | undefined;
    const getController = () => controller;

    const refreshSnapshot = async (signal: AbortSignal) => {
      const snapshot = await refreshNotificationInbox(queryClient, true);
      signal.throwIfAborted();
      const cursor = snapshot?.pagination?.liveUpdateCursor;
      if (!cursor)
        throw new Error("Notification snapshot omitted its live cursor");
      return { cursor };
    };

    const bootstrap = async () => {
      const abortController = new AbortController();
      try {
        const snapshot = await refreshSnapshot(abortController.signal);
        if (disposed) return;
        bootstrapAttempt = 0;
        setHasSnapshot(true);
        controller = createDurableLiveUpdatesController({
          buildUrl: buildNotificationInboxStreamUrl,
          getAccessToken,
          initialCursor: snapshot.cursor,
          onChanges: async (
            changes: ReceivedNotificationChange[],
            signal: AbortSignal,
          ) => {
            const generation = connectionGeneration;
            const createdIds = new Set(
              changes
                .filter(
                  (change) =>
                    change.type === "notification.created" &&
                    change.connectionGeneration === generation &&
                    !presentedIds.has(change.notificationId),
                )
                .map((change) => change.notificationId),
            );
            const inbox = await refreshNotificationInbox(
              queryClient,
              createdIds.size > 0,
              changes.every((change) => change.type === "notification.created"),
            );
            signal.throwIfAborted();
            if (!createdIds.size || !inbox) return;

            // Reuse the canonical response refreshed in this cycle for toasts.
            if (
              disposed ||
              signal.aborted ||
              generation !== connectionGeneration ||
              getController()?.getStatus() !== "ready"
            )
              return;

            const notifications = inbox.data.notifications.filter(
              (notification) =>
                createdIds.has(notification.id) && !notification.readAt,
            );
            createdIds.forEach((id) => presentedIds.add(id));
            // Bound session memory and avoid overwhelming the screen during bursts.
            while (presentedIds.size > 1000) {
              const oldest = presentedIds.values().next().value;
              if (oldest) presentedIds.delete(oldest);
            }
            onLiveNotificationsRef.current?.(
              notifications.slice(0, 3).reverse(),
            );
          },
          onStatusChange: (status) => {
            if (status !== "ready") {
              connectionGeneration += 1;
              return;
            }
            // Each connection starts live-only. Catch up badges and open inboxes
            // without presenting anything created while the socket was offline.
            void refreshNotificationInbox(queryClient).catch(() => {
              if (!disposed) getController()?.resync();
            });
          },
          parseFrame: (value) => {
            const result = notificationInboxStreamFrameSchema.safeParse(value);
            if (!result.success) return undefined;
            return "cursor" in result.data
              ? { ...result.data, connectionGeneration }
              : result.data;
          },
          protocol: notificationStreamSubprotocol,
          refreshSnapshot,
        });
        controller.start();
      } catch {
        if (disposed) return;
        setHasSnapshot(false);
        const delay =
          Math.random() * Math.min(1_000 * 2 ** bootstrapAttempt, 30_000);
        bootstrapAttempt += 1;
        bootstrapTimer = window.setTimeout(() => void bootstrap(), delay);
      }
    };

    void bootstrap();
    const reconnectWhenOnline = () => {
      const activeController = getController();
      if (activeController !== undefined) {
        activeController.reconnectNow();
        return;
      }
      window.clearTimeout(bootstrapTimer);
      void bootstrap();
    };
    window.addEventListener("online", reconnectWhenOnline);
    return () => {
      disposed = true;
      window.clearTimeout(bootstrapTimer);
      window.removeEventListener("online", reconnectWhenOnline);
      const activeController = getController();
      if (activeController !== undefined) activeController.stop();
    };
  }, [queryClient]);

  return hasSnapshot;
};

export const useUpdateNotificationInboxReadState = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateNotificationInboxReadState,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error marking notification as read");
    },
    onSuccess: async (_response, variables) => {
      if (!variables.silent) toast.success("Notification marked as read");
      // A failed readback must not turn a persisted PATCH into a failed action.
      // Query error state and the live stream retain their refresh recovery.
      await refreshNotificationInbox(queryClient).catch(() => undefined);
    },
  });
};

export const useUpdateNotificationInboxItemsReadState = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateNotificationInboxItemsReadState,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating notification read state");
    },
    onSuccess: async () => {
      // A failed readback must not turn a persisted PATCH into a failed action.
      // Query error state and the live stream retain their refresh recovery.
      await refreshNotificationInbox(queryClient).catch(() => undefined);
    },
  });
};

export const useDeleteNotificationInboxItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNotificationInboxItem,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting notification");
    },
    onSuccess: async () => {
      await refreshNotificationInbox(queryClient).catch(() => undefined);
    },
  });
};
